import BodyTagHandler, { BodyTagPositionFilter } from 'titus-ts/dist/js/Util/Actions/BodyTagHandler';
import ActionHandler from 'titus-ts/dist/js/Util/Actions/ActionHandler';
import Action from 'titus-ts/dist/js/Util/Actions/Action';
import { OfficeApi } from '../OfficeApi';
import FieldCodeService from 'titus-ts/dist/js/Util/Actions/FieldCodeService';

export default class OfficeBodyTaggingHandler implements ActionHandler {
    private officeApi: OfficeApi;
    private bodyTagHandler: BodyTagHandler;
    private fieldCodeService: FieldCodeService;

    constructor(officeApi: OfficeApi, fieldCodeService: FieldCodeService, bodyTagHandler?: BodyTagHandler) {
        this.officeApi = officeApi;
        this.fieldCodeService = fieldCodeService;
        this.bodyTagHandler = bodyTagHandler || new BodyTagHandler();
    }

    public handle = async (actions: Action[]): Promise<void> => {
        const updatedActions = this.fieldCodeService.process(actions, 'BodyTag');

        const { allBodyTags, existingBodyTags, replacementBodyTags } = await this.processBodyTagActions(updatedActions);

        const platform = this.officeApi.getPlatformType();
        const bodyTypeResponse = await this.officeApi.getBodyTypeAsync();
        const bodyType = bodyTypeResponse === 'html' ? 'html' : 'text';
        let existingBody = '';
        let modifiedBody = '';
        const positionFilter: BodyTagPositionFilter = {
            before: [],
            after: [],
        };

        //This call has something to do with positionFilter initialisation
        this.bodyTagHandler.getModifiedBody(existingBody, updatedActions, bodyType, positionFilter);

        if (bodyType === 'html') {
            existingBody = await this.officeApi.getBodyAsHtmlAsync();

            //Replace any existing HTML bodyTags
            modifiedBody = this.replaceExistingHtmlTags(
                existingBody,
                existingBodyTags,
                replacementBodyTags,
                updatedActions,
            );

            //Both Outlook Desktop and Web client differently mark replied message start. This is to find that beggining and insert tag in front if needed.
            if (platform === 'PC') {
                positionFilter.after.push(`<div style='border:none;border-left:solid blue 1.5pt;padding:`);
                positionFilter.after.push(`<div style='border:none;border-top:solid #e1e1e1 1.0pt;padding:`);
                positionFilter.after.push(`<div style='border:none;border-top:solid #E1E1E1 1.0pt;padding:`);
            } else {
                positionFilter.after.push(`<div id="appendonsend">`);
                positionFilter.after.push(`<hr tabindex="-1" style="display:inline-block; width:98%">
                <div id="divrplyfwdmsg" dir="ltr">`);
                positionFilter.after.push(`<div id="divRplyFwdMsg"`);
            }
        } else {
            existingBody = await this.officeApi.getBodyAsTextAsync();

            //Replace any existing plainText bodyTags
            modifiedBody = this.replaceExistingPlainTextTags(
                existingBody,
                existingBodyTags,
                replacementBodyTags,
                updatedActions,
            );
        }

        //Now add any remaining body tag actions left in updateActions
        modifiedBody = this.bodyTagHandler.getModifiedBody(
            modifiedBody,
            updatedActions,
            bodyType === 'html' ? 'html' : 'text',
            positionFilter,
        );

        const maxSetBodyLength = 1000000; // Maximum length accepted by Body.setAsync
        if (modifiedBody.length < maxSetBodyLength) {
            await this.officeApi.setBodyAsync(modifiedBody, bodyType);

            //Set the customProperties so we can compare body tags in subsequent action calls
            try {
                await this.officeApi.setOutlookCustomProperties(allBodyTags);
            } catch {
                console.log(
                    'BodyTaggingHandle : SetCustomProperties failed, body tags may be duplicated on subsequent calls.',
                );
            }
        }
    };

    private processBodyTagActions = async (
        updatedActions: Action[],
    ): Promise<{
        allBodyTags: Record<string, string>;
        existingBodyTags: Record<string, string>;
        replacementBodyTags: Record<string, string>;
    }> => {
        const allBodyTags: Record<string, string> = updatedActions.reduce(
            (pairs, action) => {
                if (action.type === 'BodyTag') {
                    pairs[action.id] = action.params.get('BodyTagContent') || '';
                }
                return pairs;
            },
            {} as Record<string, string>,
        );

        let existingBodyTags = {};
        try {
            existingBodyTags = await this.officeApi.getOutlookCustomProperties(Object.keys(allBodyTags));
        } catch {
            console.log(
                'processBodyTagActions : Get customProperties failed, body tags may be duplicated on subsequent calls.',
            );
        }
        const existingBodyTagIds = new Set(Object.keys(existingBodyTags));
        const replacementBodyTags: Record<string, string> = {};

        updatedActions.forEach((action) => {
            if (existingBodyTagIds.has(action.id)) {
                replacementBodyTags[action.id] = action.params.get('BodyTagContent') || '';
            }
        });

        return {
            allBodyTags,
            existingBodyTags,
            replacementBodyTags,
        };
    };

    private replaceExistingHtmlTags = (
        existingBody: string,
        existingHtmlTags: Record<string, string>,
        replacementHtmlTags: Record<string, string>,
        updatedActions: Action[],
    ): string => {
        const doc = new DOMParser().parseFromString(`<body>${existingBody}</body>`, 'text/html');
        const readableTextById: Record<string, string> = {};

        //Get the 'text' from the HTML tags, so we can do a text match as we can't trust the HTML hasn't been mangled!
        Object.keys(existingHtmlTags).forEach((id) => {
            const existingHtml = existingHtmlTags[id];
            if (existingHtml) {
                readableTextById[id] = this.getReadableText(existingHtml);
            }
        });

        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT, {
            acceptNode: (node: Node) => {
                return (node as Element).tagName === 'DIV' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
            },
        });
        const replacements: Array<{ element: Element; replacementHtml: string }> = [];
        const replacedElements = new Set<Element>();
        let currentNode = walker.nextNode();

        while (currentNode) {
            const element = currentNode as HTMLElement;
            const text = (element.textContent || '').replace(/\s+/g, '');

            if (text) {
                const matchingId = Object.keys(readableTextById).find((id) => readableTextById[id] === text);

                if (matchingId && !replacedElements.has(element)) {
                    replacements.push({
                        element: element,
                        replacementHtml: replacementHtmlTags[matchingId],
                    });
                    replacedElements.add(element);
                    delete readableTextById[matchingId];

                    this.removeMatchedActions(updatedActions, replacementHtmlTags[matchingId], matchingId);
                }
            }

            currentNode = walker.nextNode();
        }

        //Write the changes to the element
        replacements.forEach(({ element, replacementHtml }) => {
            element.outerHTML = replacementHtml;
        });

        //Return the entire string body back
        return doc.body.innerHTML;
    };

    private replaceExistingPlainTextTags = (
        existingBody: string,
        existingHtmlTags: Record<string, string>,
        replacementHtmlTags: Record<string, string>,
        updatedActions: Action[],
    ): string => {
        let modifiedBody = existingBody;
        const existingTextTagsById: Record<string, string> = {};
        const replacementTextTagsById: Record<string, string> = {};

        //Get text values from the existing HTML Tags
        Object.keys(existingHtmlTags).forEach((id) => {
            const existingHtml = existingHtmlTags[id];
            if (existingHtml) {
                existingTextTagsById[id] = this.getReadableText(existingHtml, true);
            }
        });

        //Get text values from the new value HTML Tags, body tags are always HTML in the config
        Object.keys(replacementHtmlTags).forEach((id) => {
            const replacementTagHtml = replacementHtmlTags[id];
            if (replacementTagHtml) {
                replacementTextTagsById[id] = this.getReadableText(replacementTagHtml, true);
            }
        });

        Object.keys(existingTextTagsById).forEach((id) => {
            const existingTagText = existingTextTagsById[id];
            const replacementTagText = replacementTextTagsById[id];

            if (existingTagText && replacementTagText && existingBody.includes(existingTagText)) {
                modifiedBody = modifiedBody.split(existingTagText).join(replacementTagText);
                delete replacementTextTagsById[id];

                this.removeMatchedActions(updatedActions, replacementHtmlTags[id], id);
            }
        });

        return modifiedBody;
    };

    private removeMatchedActions = (
        updatedActions: Action[],
        matchedContent: string | undefined,
        fallbackId: string,
    ): void => {
        const contentToMatch = matchedContent || '';

        for (let i = updatedActions.length - 1; i >= 0; i -= 1) {
            const action = updatedActions[i];
            const actionContent = action.params.get('BodyTagContent') || '';

            if ((contentToMatch && actionContent === contentToMatch) || action.id === fallbackId) {
                updatedActions.splice(i, 1);
            }
        }
    };
    private getReadableText = (html: string, plainTextStyle = false): string => {
        const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');

        if (plainTextStyle) return (doc.body.textContent || '').trim().replace(/\s+/g, ' ');
        else return (doc.body.textContent || '').replace(/\s+/g, '');
    };
}
